const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const STORAGE_KEY = 'avari_recent_booking_cue';

let activeRequest = null;
let requestKey = '';
let pendingDetail = null;

export function shouldShowRecentBookingCue(rareFindState) {
    return rareFindState?.loaded === true && rareFindState.enabled !== true;
}

function getSelectedDates(detail = null) {
    const dates =
        detail?.checkIn && detail?.checkOut
            ? `${detail.checkIn} to ${detail.checkOut}`
            : new URLSearchParams(window.location.search).get('dates') || '';
    const [checkIn, checkOut] = dates.split(' to ').map((date) => date.trim());

    if (
        !DATE_PATTERN.test(checkIn || '') ||
        !DATE_PATTERN.test(checkOut || '')
    ) {
        return null;
    }

    return { checkIn, checkOut };
}

function clearStoredCue() {
    try {
        sessionStorage.removeItem(STORAGE_KEY);
    } catch {
        // Storage can be unavailable in privacy-restricted browser contexts.
    }
}

function hide(element) {
    activeRequest?.abort();
    activeRequest = null;
    requestKey = '';
    element.classList.add('hidden');
    clearStoredCue();
}

function pushImpression(listingId, dates) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
        event: 'recent_booking_cue_impression',
        recent_booking_listing_id: listingId,
        selected_check_in: dates.checkIn,
        selected_check_out: dates.checkOut
    });
}

function storeCue(listingId, dates) {
    try {
        sessionStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({
                listingId,
                checkIn: dates.checkIn,
                checkOut: dates.checkOut
            })
        );
    } catch {
        // Attribution is optional; the public cue can still render.
    }
}

async function refresh(element, detail = null) {
    const dates = getSelectedDates(detail);
    const listingId = element?.dataset.listingId;

    if (!element || !listingId || !dates) {
        if (element) hide(element);
        return;
    }

    const rareFindState = window.__avariRareFindState;
    if (!rareFindState?.loaded) {
        pendingDetail = detail;
        hide(element);
        return;
    }
    if (!shouldShowRecentBookingCue(rareFindState)) {
        pendingDetail = null;
        hide(element);
        return;
    }
    pendingDetail = null;

    const nextRequestKey = `${listingId}:${dates.checkIn}:${dates.checkOut}`;
    if (nextRequestKey === requestKey) return;

    activeRequest?.abort();
    const controller = new AbortController();
    activeRequest = controller;
    requestKey = nextRequestKey;
    element.classList.add('hidden');
    clearStoredCue();

    const query = new URLSearchParams({
        listingId,
        checkIn: dates.checkIn,
        checkOut: dates.checkOut
    });
    if (
        new URLSearchParams(window.location.search).get(
            'recentBookingPreview'
        ) === '1'
    ) {
        query.set('preview', '1');
    }

    try {
        const response = await fetch(
            `/guesty/listing/recent-booking?${query.toString()}`,
            {
                headers: { Accept: 'application/json' },
                credentials: 'same-origin',
                signal: controller.signal
            }
        );
        const data = await response.json();

        if (
            controller !== activeRequest ||
            !response.ok ||
            !data?.enabled ||
            !data.message
        ) {
            return;
        }

        element.classList.remove('hidden');
        storeCue(listingId, dates);
        pushImpression(listingId, dates);
    } catch (error) {
        if (error?.name !== 'AbortError') {
            console.warn('Recently booked cue unavailable:', error);
        }
    } finally {
        if (controller === activeRequest) {
            activeRequest = null;
        }
    }
}

function init() {
    const element = document.querySelector(
        '#recent-booking-cue[data-listing-id]'
    );
    if (!element) return;

    if (window.__avariVerifiedListingStay) {
        refresh(element, window.__avariVerifiedListingStay);
    }
    window.addEventListener('listing-booking-dates-changed', () => {
        pendingDetail = null;
        hide(element);
    });
    window.addEventListener('listing-availability-verified', (event) => {
        refresh(element, event.detail);
    });
    window.addEventListener('listing-rare-find-state', (event) => {
        if (event.detail?.enabled) {
            pendingDetail = null;
            hide(element);
            return;
        }
        const detail =
            pendingDetail || window.__avariVerifiedListingStay || null;
        if (detail) refresh(element, detail);
    });
}

export default { init };
